/**
 * Phase 4b PR 3 — useAuthFetch hook.
 *
 * Component-friendly wrapper around the pure-function `authFetch`. The
 * hook binds the SupabaseClient from AuthContext so callers don't have
 * to thread it through manually.
 *
 * Surface defaulting: this hook always sets `X-Surface: react` unless
 * the caller explicitly overrides via `init.surface`. That's the whole
 * point of Phase 4a's telemetry — the React surface identifies itself
 * so plan_quality_events.surface partitions parity correctly.
 *
 * Usage:
 *   const fetchAuthed = useAuthFetch();
 *   const res = await fetchAuthed('/api/plans/parse-and-persist', {
 *     method: 'POST',
 *     body: JSON.stringify({ coachId: 'training-coach', responseText }),
 *   });
 */

import { useCallback } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { authFetch, type AuthFetchInit } from '../lib/authFetch';

export type AuthFetchFn = (url: string, init?: AuthFetchInit) => Promise<Response>;

export function useAuthFetch(): AuthFetchFn {
  const { supabase } = useAuthContext();

  return useCallback<AuthFetchFn>(
    (url, init = {}) => {
      // Default surface to 'react' for any call originating from the
      // React app. Callers can opt out by passing `surface: 'vanilla'`
      // (e.g., for shared helpers that mirror vanilla behavior 1:1).
      const withSurface: AuthFetchInit =
        init.surface === undefined ? { ...init, surface: 'react' } : init;
      return authFetch(supabase, url, withSurface);
    },
    [supabase],
  );
}
